import { useMemo, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge, Card } from '@/design-system/primitives'
import { cn } from '@/lib/cn'
import { useApiErrorText } from '@/lib/staffGate'

import { useSegmentChange, type SegmentChange } from '../api'
import type {
  ConductorContest,
  PerformanceChoice,
  PerformanceRow,
  ProgramSegment,
  ScreenOverlay,
  SegmentFields,
  SegmentKind,
} from '../types'
import { LiveQuiz, QuizEditor } from './Quiz'
import { text } from './shared'

const INPUT =
  'mt-1 h-11 w-full min-w-0 rounded-md border border-line bg-bg-base px-3 text-content-primary'
const AREA = 'mt-1 w-full min-w-0 rounded-md border border-line bg-bg-base px-3 py-2 text-content-primary'
const LABEL = 'block min-w-0 text-caption text-content-secondary'
const KINDS: SegmentKind[] = ['note', 'prognostic', 'announcement', 'performances', 'quiz']
const CHOICES: PerformanceChoice[] = ['sing', 'dance', 'talk']
// Les mêmes bornes que le serveur.
const MAX_BODY = 5000

/** Les sous-parties rangées par étape, dans leur ordre. */
export function segmentsByItem(segments: ProgramSegment[] | undefined) {
  const grouped = new Map<string, ProgramSegment[]>()
  for (const segment of segments ?? []) {
    const list = grouped.get(segment.program_item_id) ?? []
    list.push(segment)
    grouped.set(segment.program_item_id, list)
  }
  return grouped
}

function KindBadge({ kind }: { kind: SegmentKind }) {
  const { t } = useTranslation()
  const tone =
    kind === 'note' ? 'neutral' : kind === 'performances' || kind === 'quiz' ? 'champagne' : 'ember'
  return <Badge tone={tone}>{t(`conductor.segments.kind.${kind}`)}</Badge>
}

function OnScreenBadge({ segment }: { segment: ProgramSegment }) {
  const { t } = useTranslation()
  if (!segment.on_screen) return null
  return (
    <Badge tone="success">
      {segment.on_screen === 'candidates'
        ? t('conductor.segments.onScreenCandidates')
        : segment.on_screen === 'quiz'
          ? t('conductor.segments.onScreenQuiz')
          : t('conductor.segments.onScreenWinner')}
    </Badge>
  )
}

/** Un texte long du MC, lu tel qu'il l'a tapé — retours à la ligne compris. */
function Body({ value, className }: { value: string | null; className?: string }) {
  if (!value) return null
  return (
    <p className={cn('whitespace-pre-line break-words text-sm text-content-secondary', className)}>
      {value}
    </p>
  )
}

// ── Préparer : la liste d'une étape, dans l'onglet Programme ───────

/**
 * Les sous-parties d'une étape, à préparer avant la soirée.
 *
 * Privées : ni le programme public ni l'écran de la salle ne les lisent. Le
 * MC y range ce qu'il tenait sur papier — pronostic, prestations, désignations
 * dans la salle, textes à lire au micro.
 */
export function SegmentEditor({
  eventId,
  itemId,
  segments,
  contests,
  can,
}: {
  eventId: string | undefined
  itemId: string
  segments: ProgramSegment[]
  contests: ConductorContest[]
  can: (permission: string) => boolean
}) {
  const { t } = useTranslation()
  const errorText = useApiErrorText()
  const change = useSegmentChange(eventId)
  const [open, setOpen] = useState<
    | { kind: 'edit'; id: string }
    | { kind: 'delete'; id: string }
    | { kind: 'create'; segmentKind: SegmentKind | null }
    | null
  >(null)

  const canCreate = can('program.item.create')
  const canUpdate = can('program.item.update')
  const canDelete = can('program.item.delete')
  const canReorder = can('program.item.reorder')

  const run = (payload: SegmentChange) => {
    change.mutate(payload, { onSuccess: () => setOpen(null) })
  }

  return (
    <div className="mt-4 space-y-3 border-t border-line-subtle pt-4">
      <p className="overline text-content-muted">{t('conductor.segments.title')}</p>
      <p className="text-caption text-content-muted">{t('conductor.segments.privateHint')}</p>

      {change.isError ? (
        <p role="alert" className="text-sm text-state-danger">
          {errorText(change.error)}
        </p>
      ) : null}

      {segments.length === 0 ? (
        <p className="text-sm text-content-secondary">{t('conductor.segments.empty')}</p>
      ) : null}

      <ol className="space-y-3">
        {segments.map((segment, index) => (
          <li key={segment.id} className="rounded-md border border-line-subtle p-3">
            <div className="flex flex-wrap items-center gap-2">
              <KindBadge kind={segment.kind} />
              <OnScreenBadge segment={segment} />
            </div>
            <p className="mt-2 break-words font-display text-content-primary">{segment.title}</p>

            {open?.kind === 'edit' && open.id === segment.id ? (
              <SegmentForm
                kind={segment.kind}
                initial={segment}
                contests={contests}
                pending={change.isPending}
                onCancel={() => setOpen(null)}
                onSubmit={(fields) => run({ kind: 'update', id: segment.id, fields })}
              />
            ) : (
              <>
                <Body value={segment.body} className="mt-1" />
                {segment.kind === 'quiz' ? (
                  <QuizEditor eventId={eventId} segment={segment} can={can} />
                ) : null}
                {segment.kind === 'performances' ? (
                  <PerformanceEditor
                    eventId={eventId}
                    segment={segment}
                    contests={contests}
                    canEdit={canUpdate}
                  />
                ) : null}
                {(segment.kind === 'prognostic' || segment.kind === 'announcement') &&
                segment.winner ? (
                  <p className="mt-2 break-words text-sm text-champagne-300">
                    {t('conductor.segments.winnerIs', { name: segment.winner })}
                    {segment.winner_detail ? (
                      <span className="text-content-muted"> · {segment.winner_detail}</span>
                    ) : null}
                  </p>
                ) : null}
              </>
            )}

            {open?.kind === 'delete' && open.id === segment.id ? (
              <div className="mt-3 rounded-md border border-state-danger/40 p-3">
                <p className="break-words text-sm text-content-primary">
                  {t('conductor.segments.deleteConfirm', { title: segment.title })}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    disabled={change.isPending}
                    onClick={() => run({ kind: 'delete', id: segment.id })}
                  >
                    {t('conductor.plan.deleteYes')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setOpen(null)}>
                    {t('conductor.plan.cancel')}
                  </Button>
                </div>
              </div>
            ) : open?.kind === 'edit' && open.id === segment.id ? null : (
              <div className="mt-3 flex flex-wrap gap-2">
                {canReorder ? (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      aria-label={t('conductor.plan.moveUp')}
                      title={t('conductor.plan.moveUp')}
                      disabled={change.isPending || index === 0}
                      onClick={() => run({ kind: 'move', id: segment.id, direction: 'up' })}
                    >
                      ↑
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      aria-label={t('conductor.plan.moveDown')}
                      title={t('conductor.plan.moveDown')}
                      disabled={change.isPending || index === segments.length - 1}
                      onClick={() => run({ kind: 'move', id: segment.id, direction: 'down' })}
                    >
                      ↓
                    </Button>
                  </>
                ) : null}
                {canUpdate ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setOpen({ kind: 'edit', id: segment.id })}
                  >
                    {t('conductor.plan.edit')}
                  </Button>
                ) : null}
                {canDelete ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-state-danger"
                    onClick={() => setOpen({ kind: 'delete', id: segment.id })}
                  >
                    {t('conductor.plan.delete')}
                  </Button>
                ) : null}
              </div>
            )}
          </li>
        ))}
      </ol>

      {canCreate ? (
        open?.kind === 'create' ? (
          <div className="rounded-md border border-champagne-400/40 p-3">
            <p className="text-caption text-content-secondary">{t('conductor.segments.pickKind')}</p>
            <div
              role="radiogroup"
              aria-label={t('conductor.segments.pickKind')}
              className="mt-2 grid grid-cols-1 gap-2 min-[400px]:grid-cols-2"
            >
              {KINDS.map((kind) => (
                <button
                  key={kind}
                  type="button"
                  role="radio"
                  aria-checked={open.segmentKind === kind}
                  onClick={() => setOpen({ kind: 'create', segmentKind: kind })}
                  className={cn(
                    'min-h-11 min-w-0 rounded-md border px-3 py-2 text-left text-sm transition',
                    open.segmentKind === kind
                      ? 'border-champagne-400/60 bg-champagne-400/10 text-champagne-300'
                      : 'border-line-subtle text-content-secondary hover:text-content-primary',
                  )}
                >
                  <span className="block break-words font-medium">
                    {t(`conductor.segments.kind.${kind}`)}
                  </span>
                  <span className="mt-0.5 block break-words text-caption text-content-muted">
                    {t(`conductor.segments.kindHint.${kind}`)}
                  </span>
                </button>
              ))}
            </div>
            {open.segmentKind ? (
              <SegmentForm
                key={open.segmentKind}
                kind={open.segmentKind}
                initial={null}
                contests={contests}
                pending={change.isPending}
                onCancel={() => setOpen(null)}
                onSubmit={(fields) =>
                  run({
                    kind: 'create',
                    itemId,
                    segmentKind: open.segmentKind as SegmentKind,
                    fields: fields as SegmentFields,
                  })
                }
              />
            ) : (
              <Button className="mt-3" size="sm" variant="ghost" onClick={() => setOpen(null)}>
                {t('conductor.plan.cancel')}
              </Button>
            )}
          </div>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="w-full sm:w-auto"
            onClick={() => setOpen({ kind: 'create', segmentKind: null })}
          >
            {t('conductor.segments.add')}
          </Button>
        )
      ) : null}
    </div>
  )
}

/**
 * Créer ou modifier une sous-partie — les champs de son type, et eux seuls.
 *
 * En modification, seuls les champs changés partent au serveur.
 */
function SegmentForm({
  kind,
  initial,
  contests,
  pending,
  onSubmit,
  onCancel,
}: {
  kind: SegmentKind
  initial: ProgramSegment | null
  contests: ConductorContest[]
  pending: boolean
  onSubmit: (fields: Partial<SegmentFields>) => void
  onCancel: () => void
}) {
  const { t, i18n } = useTranslation()
  const [title, setTitle] = useState(
    initial?.title ??
      (kind === 'prognostic' || kind === 'performances' || kind === 'quiz'
        ? t(`conductor.segments.defaultTitle.${kind}`)
        : ''),
  )
  const [body, setBody] = useState(initial?.body ?? '')
  const [categoryId, setCategoryId] = useState(
    initial?.competition_category_id ?? contests[0]?.id ?? '',
  )
  const [winner, setWinner] = useState(initial?.winner ?? '')
  const [detail, setDetail] = useState(initial?.winner_detail ?? '')
  const [error, setError] = useState<string | null>(null)
  const withWinner = kind === 'prognostic' || kind === 'announcement'

  const submit = (formEvent: FormEvent) => {
    formEvent.preventDefault()
    setError(null)
    if (!title.trim()) {
      setError(t('conductor.segments.error.title'))
      return
    }
    if (kind === 'performances' && !categoryId) {
      setError(t('conductor.segments.error.contest'))
      return
    }
    const fields: SegmentFields = {
      title: title.trim(),
      body: kind === 'performances' ? null : body.trim() || null,
      competition_category_id: kind === 'performances' ? categoryId : null,
      winner: withWinner ? winner.trim() || null : null,
      winner_detail: withWinner ? detail.trim() || null : null,
    }
    if (!initial) {
      onSubmit(fields)
      return
    }
    const changed: Partial<SegmentFields> = {}
    if (fields.title !== initial.title) changed.title = fields.title
    if (kind !== 'performances' && fields.body !== (initial.body ?? null)) changed.body = fields.body
    if (kind === 'performances' && categoryId !== initial.competition_category_id) {
      changed.competition_category_id = categoryId
    }
    if (withWinner && fields.winner !== (initial.winner ?? null)) changed.winner = fields.winner
    if (withWinner && fields.winner_detail !== (initial.winner_detail ?? null)) {
      changed.winner_detail = fields.winner_detail
    }
    if (Object.keys(changed).length === 0) {
      onCancel()
      return
    }
    onSubmit(changed)
  }

  return (
    <form className="mt-3 space-y-3" onSubmit={submit}>
      <label className={LABEL}>
        {t('conductor.segments.field.title')}
        <input
          value={title}
          maxLength={200}
          autoFocus={!initial}
          placeholder={kind === 'announcement' ? t('conductor.segments.placeholder.announcement') : ''}
          onChange={(change) => setTitle(change.target.value)}
          className={INPUT}
        />
      </label>

      {kind === 'performances' ? (
        <label className={LABEL}>
          {t('conductor.segments.field.contest')}
          <select
            value={categoryId}
            onChange={(change) => setCategoryId(change.target.value)}
            className={INPUT}
          >
            {contests.map((contest) => (
              <option key={contest.id} value={contest.id}>
                {text(contest.name_i18n, i18n.language)}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <label className={LABEL}>
          {kind === 'note'
            ? t('conductor.segments.field.body')
            : t('conductor.segments.field.instructions')}
          <textarea
            rows={kind === 'note' ? 6 : 3}
            value={body}
            maxLength={MAX_BODY}
            onChange={(change) => setBody(change.target.value)}
            className={AREA}
          />
        </label>
      )}

      {withWinner ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className={LABEL}>
            {t('conductor.segments.field.winner')}
            <input
              value={winner}
              maxLength={200}
              placeholder={t('conductor.segments.placeholder.winner')}
              onChange={(change) => setWinner(change.target.value)}
              className={INPUT}
            />
          </label>
          <label className={LABEL}>
            {t('conductor.segments.field.detail')}
            <input
              value={detail}
              maxLength={300}
              onChange={(change) => setDetail(change.target.value)}
              className={INPUT}
            />
          </label>
        </div>
      ) : null}

      {error ? (
        <p role="alert" className="text-sm text-state-danger">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" disabled={pending}>
          {t('conductor.plan.save')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          {t('conductor.plan.cancel')}
        </Button>
      </div>
    </form>
  )
}

// ── Prestations : chanter, danser ou parler ─────────────────────────

function CandidateFace({ row }: { row: PerformanceRow }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      {row.photo_url ? (
        <img
          src={row.photo_url}
          alt=""
          className="h-10 w-10 shrink-0 rounded-full object-cover"
          loading="lazy"
        />
      ) : (
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-line-subtle font-mono text-sm text-champagne-300">
          {row.number ?? '·'}
        </span>
      )}
      <p className="min-w-0 break-words text-content-primary">
        {row.number !== null ? (
          <span className="mr-1 font-mono tabular-nums text-champagne-300">{row.number}.</span>
        ) : null}
        {row.name}
      </p>
    </div>
  )
}

function contestName(segment: ProgramSegment, contests: ConductorContest[], locale: string) {
  const contest = contests.find((row) => row.id === segment.competition_category_id)
  return contest ? text(contest.name_i18n, locale) : null
}

/** Ce que chaque candidat a choisi, à saisir avant la soirée. */
function PerformanceEditor({
  eventId,
  segment,
  contests,
  canEdit,
}: {
  eventId: string | undefined
  segment: ProgramSegment
  contests: ConductorContest[]
  canEdit: boolean
}) {
  const { t, i18n } = useTranslation()
  const name = contestName(segment, contests, i18n.language)
  const missing = segment.performances.filter((row) => !row.choice).length

  return (
    <div className="mt-2 space-y-2">
      <p className="text-caption text-content-muted">
        {name ? `${name} · ` : ''}
        {missing > 0
          ? t('conductor.segments.performancesMissing', { count: missing })
          : t('conductor.segments.performancesComplete')}
      </p>
      {segment.performances.length === 0 ? (
        <p className="text-sm text-content-secondary">{t('conductor.segments.noCandidates')}</p>
      ) : null}
      <ul className="space-y-2">
        {segment.performances.map((row) => (
          <li key={row.candidate_id} className="rounded-md bg-bg-inset p-2">
            {canEdit ? (
              <PerformanceRowForm eventId={eventId} row={row} />
            ) : (
              <PerformanceLine row={row} />
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

function PerformanceRowForm({ eventId, row }: { eventId: string | undefined; row: PerformanceRow }) {
  const { t } = useTranslation()
  const errorText = useApiErrorText()
  const change = useSegmentChange(eventId)
  const [choice, setChoice] = useState<PerformanceChoice | ''>(row.choice ?? '')
  const [topic, setTopic] = useState(row.topic ?? '')
  const [notes, setNotes] = useState(row.notes ?? '')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const dirty =
    choice !== (row.choice ?? '') ||
    (choice === 'talk' && topic.trim() !== (row.topic ?? '')) ||
    notes.trim() !== (row.notes ?? '')

  const save = (formEvent: FormEvent) => {
    formEvent.preventDefault()
    setError(null)
    if (choice === 'talk' && !topic.trim()) {
      setError(t('conductor.segments.error.topic'))
      return
    }
    change.mutate(
      {
        kind: 'performance',
        candidateId: row.candidate_id,
        choice: choice || null,
        topic: choice === 'talk' ? topic.trim() : null,
        notes: notes.trim() || null,
      },
      {
        onSuccess: () => {
          setSaved(true)
          window.setTimeout(() => setSaved(false), 2000)
        },
      },
    )
  }

  return (
    <form onSubmit={save} className="space-y-2">
      <CandidateFace row={row} />
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[12rem_1fr]">
        <label className={LABEL}>
          {t('conductor.segments.field.choice')}
          <select
            value={choice}
            onChange={(event) => setChoice(event.target.value as PerformanceChoice | '')}
            className={INPUT}
          >
            <option value="">{t('conductor.segments.choice.none')}</option>
            {CHOICES.map((value) => (
              <option key={value} value={value}>
                {t(`conductor.segments.choice.${value}`)}
              </option>
            ))}
          </select>
        </label>
        {choice === 'talk' ? (
          <label className={LABEL}>
            {t('conductor.segments.field.topic')}
            <input
              value={topic}
              maxLength={300}
              required
              onChange={(event) => setTopic(event.target.value)}
              className={INPUT}
            />
          </label>
        ) : null}
      </div>
      <label className={LABEL}>
        {t('conductor.segments.field.performanceNotes')}
        <input
          value={notes}
          maxLength={500}
          placeholder={t('conductor.segments.placeholder.performanceNotes')}
          onChange={(event) => setNotes(event.target.value)}
          className={INPUT}
        />
      </label>
      {error || change.isError ? (
        <p role="alert" className="text-sm text-state-danger">
          {error ?? errorText(change.error)}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" size="sm" variant="outline" disabled={!dirty || change.isPending}>
          {t('conductor.plan.save')}
        </Button>
        {saved ? (
          <span className="text-caption text-state-success">{t('conductor.segments.saved')}</span>
        ) : null}
      </div>
    </form>
  )
}

/** La ligne que le MC lit au micro : qui, et ce qu'il va faire. */
function PerformanceLine({ row }: { row: PerformanceRow }) {
  const { t } = useTranslation()
  return (
    <div className="space-y-1">
      <CandidateFace row={row} />
      <p className="break-words pl-[3.25rem] text-sm">
        {row.choice ? (
          <span className="font-medium text-champagne-300">
            {t(`conductor.segments.choice.${row.choice}`)}
          </span>
        ) : (
          <span className="text-state-warning">{t('conductor.segments.choice.none')}</span>
        )}
        {row.choice === 'talk' && row.topic ? (
          <span className="text-content-primary"> — {row.topic}</span>
        ) : null}
      </p>
      {row.notes ? (
        <p className="break-words pl-[3.25rem] text-caption text-content-muted">{row.notes}</p>
      ) : null}
    </div>
  )
}

// ── Dérouler : la feuille de l'étape en cours, dans l'onglet Direct ─

/**
 * Les sous-parties d'une étape, telles que le MC les lit pendant la soirée.
 *
 * Lecture d'abord, et les seuls gestes du moment : afficher les candidats du
 * pronostic, noter et publier un gagnant, retirer de l'écran.
 */
export function LiveSegments({
  eventId,
  segments,
  contests,
  can,
}: {
  eventId: string | undefined
  segments: ProgramSegment[]
  contests: ConductorContest[]
  can: (permission: string) => boolean
}) {
  const errorText = useApiErrorText()
  const change = useSegmentChange(eventId)
  if (segments.length === 0) return null

  return (
    <div className="space-y-3">
      {change.isError ? (
        <p role="alert" className="text-sm text-state-danger">
          {errorText(change.error)}
        </p>
      ) : null}
      {segments.map((segment) => (
        <LiveSegment
          key={segment.id}
          segment={segment}
          contests={contests}
          can={can}
          pending={change.isPending}
          onChange={(payload, after) =>
            change.mutate(payload, after ? { onSuccess: () => after() } : {})
          }
        />
      ))}
    </div>
  )
}

function LiveSegment({
  segment,
  contests,
  can,
  pending,
  onChange,
}: {
  segment: ProgramSegment
  contests: ConductorContest[]
  can: (permission: string) => boolean
  pending: boolean
  onChange: (payload: SegmentChange, after?: () => void) => void
}) {
  const { t, i18n } = useTranslation()
  const canScreen = can('program.screen.control')
  const canUpdate = can('program.item.update')
  const name = contestName(segment, contests, i18n.language)

  return (
    <div
      className={cn(
        'rounded-md border p-3',
        segment.on_screen ? 'border-state-success/60' : 'border-line-subtle',
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <KindBadge kind={segment.kind} />
        <OnScreenBadge segment={segment} />
      </div>
      <p className="mt-2 break-words font-display text-heading-sm text-content-primary">
        {segment.title}
      </p>
      <Body value={segment.body} className="mt-1 text-content-primary" />

      {segment.kind === 'performances' ? (
        <div className="mt-2 space-y-2">
          {name ? <p className="text-caption text-content-muted">{name}</p> : null}
          {segment.performances.length === 0 ? (
            <p className="text-sm text-content-secondary">{t('conductor.segments.noCandidates')}</p>
          ) : (
            <ul className="space-y-3">
              {segment.performances.map((row) => (
                <li key={row.candidate_id}>
                  <PerformanceLine row={row} />
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {segment.kind === 'quiz' ? (
        <LiveQuiz segment={segment} canScreen={canScreen} pending={pending} onChange={onChange} />
      ) : null}

      {segment.kind === 'prognostic' && canScreen ? (
        <CandidatesOnScreen
          segment={segment}
          contests={contests}
          pending={pending}
          onChange={onChange}
        />
      ) : null}

      {segment.kind === 'prognostic' || segment.kind === 'announcement' ? (
        <WinnerControl
          key={`${segment.id}-${segment.winner ?? ''}-${segment.winner_detail ?? ''}`}
          segment={segment}
          canScreen={canScreen}
          canUpdate={canUpdate}
          pending={pending}
          onChange={onChange}
        />
      ) : null}
    </div>
  )
}

/** Pronostic : montrer à la salle les candidats d'un ou plusieurs concours. */
function CandidatesOnScreen({
  segment,
  contests,
  pending,
  onChange,
}: {
  segment: ProgramSegment
  contests: ConductorContest[]
  pending: boolean
  onChange: (payload: SegmentChange, after?: () => void) => void
}) {
  const { t, i18n } = useTranslation()
  const [chosen, setChosen] = useState<string[]>(() => contests.map((row) => row.id))
  const picked = contests.filter((row) => chosen.includes(row.id)).map((row) => row.id)
  const live = segment.on_screen === 'candidates'

  const toggle = (id: string) =>
    setChosen((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    )

  return (
    <div className="mt-3 space-y-2 rounded-md bg-bg-inset p-3">
      <p className="text-caption text-content-secondary">{t('conductor.segments.showWhich')}</p>
      {contests.length > 1 ? (
        <div className="flex flex-wrap gap-2">
          {contests.map((contest) => (
            <button
              key={contest.id}
              type="button"
              aria-pressed={chosen.includes(contest.id)}
              onClick={() => toggle(contest.id)}
              className={cn(
                'min-h-10 rounded-full border px-4 py-2 text-sm transition',
                chosen.includes(contest.id)
                  ? 'border-champagne-400/60 bg-champagne-400/10 text-champagne-300'
                  : 'border-line-subtle text-content-secondary hover:text-content-primary',
              )}
            >
              {text(contest.name_i18n, i18n.language)}
            </button>
          ))}
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant={live ? 'outline' : 'ember'}
          className="min-w-0 whitespace-normal text-left"
          disabled={pending || picked.length === 0}
          onClick={() =>
            onChange({ kind: 'show', id: segment.id, show: 'candidates', categoryIds: picked })
          }
        >
          {live ? t('conductor.segments.showCandidatesAgain') : t('conductor.segments.showCandidates')}
        </Button>
        {live ? (
          <Button size="sm" variant="ghost" disabled={pending} onClick={() => onChange({ kind: 'clear' })}>
            {t('conductor.segments.removeFromScreen')}
          </Button>
        ) : null}
      </div>
    </div>
  )
}

/** Noter le gagnant (table ou personne), le publier à l'écran, le retirer. */
function WinnerControl({
  segment,
  canScreen,
  canUpdate,
  pending,
  onChange,
}: {
  segment: ProgramSegment
  canScreen: boolean
  canUpdate: boolean
  pending: boolean
  onChange: (payload: SegmentChange, after?: () => void) => void
}) {
  const { t } = useTranslation()
  const [winner, setWinner] = useState(segment.winner ?? '')
  const [detail, setDetail] = useState(segment.winner_detail ?? '')
  const live = segment.on_screen === 'announcement'
  const dirty =
    winner.trim() !== (segment.winner ?? '') || detail.trim() !== (segment.winner_detail ?? '')

  // Publier enregistre d'abord ce qui vient d'être tapé : le MC ne doit pas
  // avoir à penser « enregistrer » puis « publier » micro en main.
  const publish = () => {
    const show = () => onChange({ kind: 'show', id: segment.id, show: 'winner' })
    if (dirty) {
      onChange(
        {
          kind: 'update',
          id: segment.id,
          fields: { winner: winner.trim() || null, winner_detail: detail.trim() || null },
        },
        show,
      )
    } else {
      show()
    }
  }

  if (!canUpdate && !canScreen) {
    return segment.winner ? (
      <p className="mt-3 break-words text-sm text-champagne-300">
        {t('conductor.segments.winnerIs', { name: segment.winner })}
      </p>
    ) : null
  }

  return (
    <div className="mt-3 space-y-2 rounded-md bg-bg-inset p-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label className={LABEL}>
          {t('conductor.segments.field.winner')}
          <input
            value={winner}
            maxLength={200}
            disabled={!canUpdate}
            placeholder={t('conductor.segments.placeholder.winner')}
            onChange={(event) => setWinner(event.target.value)}
            className={INPUT}
          />
        </label>
        <label className={LABEL}>
          {t('conductor.segments.field.detail')}
          <input
            value={detail}
            maxLength={300}
            disabled={!canUpdate}
            onChange={(event) => setDetail(event.target.value)}
            className={INPUT}
          />
        </label>
      </div>
      <div className="flex flex-wrap gap-2">
        {canUpdate && dirty ? (
          <Button
            size="sm"
            variant="outline"
            disabled={pending}
            onClick={() =>
              onChange({
                kind: 'update',
                id: segment.id,
                fields: { winner: winner.trim() || null, winner_detail: detail.trim() || null },
              })
            }
          >
            {t('conductor.plan.save')}
          </Button>
        ) : null}
        {canScreen ? (
          <Button
            size="sm"
            variant={live && !dirty ? 'outline' : 'ember'}
            className="min-w-0 whitespace-normal text-left"
            disabled={pending || !winner.trim()}
            onClick={publish}
          >
            {live && !dirty
              ? t('conductor.segments.publishWinnerAgain')
              : t('conductor.segments.publishWinner')}
          </Button>
        ) : null}
        {canScreen && live ? (
          <Button size="sm" variant="ghost" disabled={pending} onClick={() => onChange({ kind: 'clear' })}>
            {t('conductor.segments.removeFromScreen')}
          </Button>
        ) : null}
      </div>
    </div>
  )
}

// ── Ce qui est à l'écran en ce moment ──────────────────────────────

/**
 * L'affichage du MC en cours sur la télé, et le geste pour le retirer.
 *
 * Montré dans le Direct et dans l'onglet Écran : un pronostic oublié à
 * l'écran cacherait le programme toute la soirée.
 */
export function OverlayBanner({
  eventId,
  overlay,
  can,
}: {
  eventId: string | undefined
  overlay: ScreenOverlay | null | undefined
  can: (permission: string) => boolean
}) {
  const { t } = useTranslation()
  const errorText = useApiErrorText()
  const change = useSegmentChange(eventId)
  const summary = useMemo(() => {
    if (!overlay) return null
    if (overlay.kind === 'announcement') return overlay.winner
    if (overlay.kind === 'quiz') {
      return t('conductor.quiz.summary', {
        number: overlay.number,
        total: overlay.total,
        question: overlay.question,
      })
    }
    return overlay.categories.map((category) => category.name).join(' · ')
  }, [overlay, t])
  if (!overlay) return null

  return (
    <Card className="border-state-success/60 p-4">
      <p className="overline text-state-success">{t('conductor.segments.liveOnScreen')}</p>
      <p className="mt-1 break-words font-display text-heading-sm text-content-primary">
        {overlay.title}
      </p>
      {summary ? <p className="mt-1 break-words text-sm text-content-secondary">{summary}</p> : null}
      {change.isError ? (
        <p role="alert" className="mt-2 text-sm text-state-danger">
          {errorText(change.error)}
        </p>
      ) : null}
      {can('program.screen.control') ? (
        <Button
          className="mt-3 w-full sm:w-auto"
          variant="outline"
          disabled={change.isPending}
          onClick={() => change.mutate({ kind: 'clear' })}
        >
          {t('conductor.segments.clearScreen')}
        </Button>
      ) : null}
    </Card>
  )
}
